import type { CalendarDate } from '@internationalized/date';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { Color } from '../../../../types/colors';
import type { DatePickerValidationState } from '../types';
import { parseDateForLocale } from '../utils';

interface UseValidationManagerOptions {
  externalState?: DatePickerValidationState | null;
  externalMessage?: string;
  locale: string;
  dateInputCultureInfo: string;
  dateFormat?: string;
  isDateUnavailable?: (date: CalendarDate) => boolean;
  isControlled: boolean;
}

export interface ValidationResult {
  parsedDate?: CalendarDate;
  isValid: boolean;
  state: DatePickerValidationState | null;
  message: string;
}

const VALIDATION_STATE_TO_COLOR: Record<NonNullable<DatePickerValidationState>, Color> = {
  valid: 'success',
  invalid: 'danger',
  edited: 'info',
  warning: 'warning',
} as const;

/**
 * Hook to manage validation state and logic for SgDatePicker
 * Centralizes all validation logic in one place
 *
 * @param options - Validation configuration
 * @returns Validation state and methods
 */
export const useValidationManager = (options: UseValidationManagerOptions) => {
  const { formatMessage } = useIntl();
  const { externalState, externalMessage, locale, dateInputCultureInfo, isDateUnavailable, isControlled } = options;

  const [internalState, setInternalState] = useState<DatePickerValidationState | null>(null);
  const [internalMessage, setInternalMessage] = useState<string>('');

  const validationState = isControlled ? externalState : internalState;
  const validationMessage = isControlled ? externalMessage : internalMessage;

  useEffect(() => {
    if (externalState !== undefined) {
      setInternalState(externalState);
    }
  }, [externalState]);

  useEffect(() => {
    if (externalMessage !== undefined) {
      setInternalMessage(externalMessage);
    }
  }, [externalMessage]);

  const validateDateInput = useCallback(
    (dateString: string): ValidationResult => {
      if (!dateString || dateString.trim() === '') {
        return {
          isValid: true,
          state: null,
          message: '',
        };
      }

      const parsedDate = parseDateForLocale(dateString.trim(), locale, dateInputCultureInfo);

      if (!parsedDate) {
        return {
          isValid: false,
          state: 'invalid',
          message: formatMessage({ id: 'sgds.date.expectedDate' }),
        };
      }

      if (isDateUnavailable?.(parsedDate)) {
        return {
          parsedDate,
          isValid: false,
          state: 'invalid',
          message: formatMessage({ id: 'sgds.date.disabledDate' }),
        };
      }

      return {
        parsedDate,
        isValid: true,
        state: 'valid',
        message: '',
      };
    },
    [locale, dateInputCultureInfo, isDateUnavailable, formatMessage],
  );

  const setValidation = useCallback(
    (state: DatePickerValidationState | null, message = '') => {
      if (!isControlled) {
        setInternalState(state);
        setInternalMessage(message);
      }
    },
    [isControlled],
  );

  const clearValidation = useCallback(() => {
    if (!isControlled) {
      setInternalState(null);
      setInternalMessage('');
    }
  }, [isControlled]);

  const getValidationColor = useCallback((): Color => {
    if (!validationState) {
      return 'danger';
    }

    return VALIDATION_STATE_TO_COLOR[validationState];
  }, [validationState]);

  return useMemo(() => {
    return {
      validationState,
      validationMessage,
      validateDateInput,
      setValidation,
      clearValidation,
      getValidationColor,
    };
  }, [validationState, validationMessage, validateDateInput, setValidation, clearValidation, getValidationColor]);
};
